import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, ClipboardCheck, KeyRound, PackagePlus, Check, X, Loader2, AlertTriangle, Copy, ShoppingCart, Send, Download, Lightbulb, Package, Trophy, LineChart as LineChartIcon, Search } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { supplierCatalogsApi, supplierPortalAdminApi, supplierPurchaseOrdersApi } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { useAuth } from '@/contexts/AuthContext'

type Tab = 'suppliers' | 'orders' | 'proposals' | 'prices' | 'ranking' | 'review'

const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d']

export default function SupplierPortalAdminPage() {
  const [tab, setTab] = useState<Tab>('suppliers')

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-1">Portal de Proveedores</h1>
      <p className="text-slate-500 mb-5">
        Gestiona accesos de proveedores asiáticos y revisa sus cotizaciones de precio/disponibilidad.
      </p>

      <div className="flex gap-1 border-b border-slate-200 mb-6">
        <TabButton active={tab === 'suppliers'} onClick={() => setTab('suppliers')} icon={<Users size={16} />}>
          Proveedores y accesos
        </TabButton>
        <TabButton active={tab === 'orders'} onClick={() => setTab('orders')} icon={<ShoppingCart size={16} />}>
          Pedidos de compra
        </TabButton>
        <TabButton active={tab === 'proposals'} onClick={() => setTab('proposals')} icon={<Lightbulb size={16} />}>
          Propuestas
        </TabButton>
        <TabButton active={tab === 'prices'} onClick={() => setTab('prices')} icon={<LineChartIcon size={16} />}>
          Histórico precios
        </TabButton>
        <TabButton active={tab === 'ranking'} onClick={() => setTab('ranking')} icon={<Trophy size={16} />}>
          Ranking
        </TabButton>
        <TabButton active={tab === 'review'} onClick={() => setTab('review')} icon={<ClipboardCheck size={16} />}>
          Cambios de precio
        </TabButton>
      </div>

      {tab === 'suppliers' && <SuppliersTab />}
      {tab === 'orders' && <OrdersTab />}
      {tab === 'proposals' && <ProposalsTab />}
      {tab === 'prices' && <PriceHistoryTab />}
      {tab === 'ranking' && <RankingTab />}
      {tab === 'review' && <ReviewTab />}
    </div>
  )
}

function TabButton({ active, onClick, icon, children }: any) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px ${
        active ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
      }`}
    >
      {icon}
      {children}
    </button>
  )
}

// ======================================================================
// TAB 1: Proveedores y accesos
// ======================================================================
function SuppliersTab() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showNew, setShowNew] = useState(false)

  const suppliersQuery = useQuery({
    queryKey: ['suppliers', 'portal'],
    queryFn: () => supplierCatalogsApi.list({ portal_only: true }),
  })

  const accountQuery = useQuery({
    queryKey: ['portal-account', selectedId],
    queryFn: () => supplierPortalAdminApi.getAccount(selectedId!).catch(() => null),
    enabled: !!selectedId,
  })

  const suppliers = suppliersQuery.data || []
  const account = accountQuery.data

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Lista proveedores */}
      <div className="bg-white rounded-xl shadow-sm p-4 lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-700">Proveedores asiáticos</h2>
          <button
            onClick={() => setShowNew(true)}
            className="text-xs px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Nuevo
          </button>
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {suppliers.map((s: any) => (
            <button
              key={s.id}
              onClick={() => setSelectedId(s.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                selectedId === s.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50 text-slate-700'
              }`}
            >
              {s.name} <span className="text-xs text-slate-400">({s.code})</span>
            </button>
          ))}
          {!suppliers.length && !suppliersQuery.isLoading && (
            <p className="text-sm text-slate-400 px-3 py-4">
              No hay proveedores asiáticos. Crea uno con "+ Nuevo".
            </p>
          )}
        </div>
      </div>

      {showNew && (
        <NewSupplierModal
          onClose={() => setShowNew(false)}
          onCreated={(newId: number) => {
            setShowNew(false)
            queryClient.invalidateQueries({ queryKey: ['suppliers', 'portal'] })
            setSelectedId(newId)
          }}
        />
      )}

      {/* Detalle / cuenta */}
      <div className="lg:col-span-2 space-y-6">
        {!selectedId ? (
          <div className="bg-white rounded-xl shadow-sm p-10 text-center text-slate-400">
            Selecciona un proveedor para gestionar su acceso al portal.
          </div>
        ) : (
          <>
            <AccountCard
              supplierId={selectedId}
              account={account}
              loading={accountQuery.isLoading}
              onChanged={() => {
                queryClient.invalidateQueries({ queryKey: ['portal-account', selectedId] })
                toast.success('Cuenta actualizada')
              }}
            />
            <AssignCard supplierId={selectedId} />
            <AssignedProductsCard supplierId={selectedId} />
            <DocumentsCard supplierId={selectedId} />
          </>
        )}
      </div>
    </div>
  )
}

function AccountCard({ supplierId, account, loading, onChanged }: any) {
  const toast = useToast()
  const [form, setForm] = useState<any>({})
  const [generated, setGenerated] = useState<string | null>(null)

  const data = { ...account, ...form }

  const createMut = useMutation({
    mutationFn: () =>
      supplierPortalAdminApi.createAccount(supplierId, {
        username: form.username,
        password: form.password || undefined,
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_whatsapp: form.contact_whatsapp,
        quote_currency: 'USD',
        portal_enabled: true,
      }),
    onSuccess: (res: any) => {
      if (res.generated_password) setGenerated(res.generated_password)
      setForm({})
      onChanged()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al crear cuenta'),
  })

  const updateMut = useMutation({
    mutationFn: () => supplierPortalAdminApi.updateAccount(supplierId, form),
    onSuccess: () => {
      if (form.password) toast.success('Contraseña restablecida')
      setForm({})
      onChanged()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al actualizar'),
  })

  if (loading) return <div className="bg-white rounded-xl shadow-sm p-6"><Loader2 className="animate-spin text-slate-400" /></div>

  const exists = !!account?.id

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-4">
        <KeyRound size={18} /> Acceso al portal
      </h3>

      {generated && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
          <p className="text-green-800 font-medium mb-1">Contraseña generada (cópiala y envíasela al proveedor):</p>
          <div className="flex items-center gap-2">
            <code className="bg-white px-2 py-1 rounded border text-slate-800">{generated}</code>
            <button onClick={() => navigator.clipboard.writeText(generated)} className="text-slate-500 hover:text-slate-700">
              <Copy size={14} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Usuario" value={data.username || ''} onChange={(v: string) => setForm({ ...form, username: v })} />
        <Field
          label={exists ? 'Resetear contraseña (opcional)' : 'Contraseña (vacío = autogenerar)'}
          value={form.password || ''}
          onChange={(v: string) => setForm({ ...form, password: v })}
          placeholder={exists ? 'Dejar vacío para no cambiar' : 'Autogenerada si vacío'}
        />
        <Field label="Empresa" value={data.company_name || ''} onChange={(v: string) => setForm({ ...form, company_name: v })} />
        <Field label="Contacto" value={data.contact_name || ''} onChange={(v: string) => setForm({ ...form, contact_name: v })} />
        <Field label="Email" value={data.contact_email || ''} onChange={(v: string) => setForm({ ...form, contact_email: v })} />
        <Field label="WhatsApp" value={data.contact_whatsapp || ''} onChange={(v: string) => setForm({ ...form, contact_whatsapp: v })} />
      </div>

      <div className="mt-4 flex items-center gap-3">
        {exists ? (
          <>
            <button
              onClick={() => updateMut.mutate()}
              disabled={updateMut.isPending}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
            >
              Guardar cambios
            </button>
            <span className={`text-xs px-2 py-1 rounded-full ${account.portal_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              {account.portal_enabled ? 'Acceso activo' : 'Acceso desactivado'}
            </span>
            <button
              onClick={() => supplierPortalAdminApi.updateAccount(supplierId, { portal_enabled: !account.portal_enabled }).then(onChanged)}
              className="text-xs text-slate-500 underline"
            >
              {account.portal_enabled ? 'Desactivar' : 'Activar'}
            </button>
          </>
        ) : (
          <button
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !form.username}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            Crear acceso
          </button>
        )}
      </div>
    </div>
  )
}

function AssignCard({ supplierId }: { supplierId: number }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [famQuery, setFamQuery] = useState('')
  const [selectedFamilies, setSelectedFamilies] = useState<{ code: string; name: string }[]>([])
  const [refQuery, setRefQuery] = useState('')
  const [debouncedRef, setDebouncedRef] = useState('')
  const [selectedRefs, setSelectedRefs] = useState<{ reference: string; name: string }[]>([])

  const familiesQuery = useQuery({ queryKey: ['factusol-families'], queryFn: () => supplierPortalAdminApi.getFamilies() })
  const families = familiesQuery.data || []

  // debounce búsqueda de referencias
  useEffect(() => {
    const t = setTimeout(() => setDebouncedRef(refQuery.trim()), 350)
    return () => clearTimeout(t)
  }, [refQuery])
  const productSearchQuery = useQuery({
    queryKey: ['factusol-product-search', debouncedRef],
    queryFn: () => supplierPortalAdminApi.searchFactusolProducts(debouncedRef),
    enabled: debouncedRef.length >= 2,
  })

  const filteredFamilies = families.filter((f: any) => {
    if (!famQuery.trim()) return true
    const q = famQuery.toLowerCase()
    return f.code.toLowerCase().includes(q) || (f.name || '').toLowerCase().includes(q)
  })
  const isFamSelected = (code: string) => selectedFamilies.some((f) => f.code === code)
  const toggleFamily = (f: any) => setSelectedFamilies((prev) => isFamSelected(f.code) ? prev.filter((x) => x.code !== f.code) : [...prev, { code: f.code, name: f.name }])
  const isRefSelected = (ref: string) => selectedRefs.some((r) => r.reference === ref)
  const toggleRef = (p: any) => setSelectedRefs((prev) => isRefSelected(p.reference) ? prev.filter((x) => x.reference !== p.reference) : [...prev, { reference: p.reference, name: p.name }])

  const mut = useMutation({
    mutationFn: () =>
      supplierPortalAdminApi.assignProducts(supplierId, {
        familias: selectedFamilies.map((f) => f.code),
        codarts: selectedRefs.map((r) => r.reference),
        with_images: true,
      }),
    onSuccess: (res: any) => {
      toast.success(`Asignados: ${res.created} nuevos, ${res.updated} actualizados (${res.images_resolved} imágenes)`)
      setSelectedFamilies([]); setSelectedRefs([]); setRefQuery(''); setFamQuery('')
      queryClient.invalidateQueries({ queryKey: ['assigned-products', supplierId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al asignar'),
  })

  const total = selectedFamilies.length + selectedRefs.length

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
        <PackagePlus size={18} /> Asignar productos a cotizar
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        Selecciona familias completas y/o busca referencias concretas. Se siembran del catálogo de Factusol.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Familias */}
        <div>
          <label className="text-sm text-slate-600 block mb-1">Familias (FAMART)</label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg w-full text-sm" placeholder="Filtrar familias…" value={famQuery} onChange={(e) => setFamQuery(e.target.value)} />
          </div>
          {selectedFamilies.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {selectedFamilies.map((f) => (
                <span key={f.code} className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                  {f.code} · {f.name}
                  <button onClick={() => toggleFamily(f)} className="hover:text-blue-900"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
            {familiesQuery.isLoading ? (
              <div className="p-3 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" size={16} /></div>
            ) : filteredFamilies.map((f: any) => (
              <button key={f.code} onClick={() => toggleFamily(f)} className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-slate-50 ${isFamSelected(f.code) ? 'bg-blue-50' : ''}`}>
                <input type="checkbox" readOnly checked={isFamSelected(f.code)} className="pointer-events-none" />
                <span className="font-mono text-xs text-slate-500 w-10">{f.code}</span>
                <span className="text-slate-700 truncate">{f.name}</span>
              </button>
            ))}
            {!familiesQuery.isLoading && !filteredFamilies.length && <p className="p-3 text-sm text-slate-400">Sin familias.</p>}
          </div>
        </div>

        {/* Referencias */}
        <div>
          <label className="text-sm text-slate-600 block mb-1">Referencias (CODART/REFART/nombre)</label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg w-full text-sm" placeholder="Buscar artículo (mín. 2 letras)…" value={refQuery} onChange={(e) => setRefQuery(e.target.value)} />
          </div>
          {selectedRefs.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {selectedRefs.map((r) => (
                <span key={r.reference} className="text-xs bg-green-100 text-green-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                  {r.reference}
                  <button onClick={() => toggleRef(r)} className="hover:text-green-900"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
            {debouncedRef.length < 2 ? (
              <p className="p-3 text-sm text-slate-400">Escribe para buscar artículos.</p>
            ) : productSearchQuery.isLoading ? (
              <div className="p-3 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" size={16} /></div>
            ) : !(productSearchQuery.data || []).length ? (
              <p className="p-3 text-sm text-slate-400">Sin resultados.</p>
            ) : (productSearchQuery.data || []).map((p: any) => (
              <button key={p.reference} onClick={() => toggleRef(p)} className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-slate-50 ${isRefSelected(p.reference) ? 'bg-green-50' : ''}`}>
                <input type="checkbox" readOnly checked={isRefSelected(p.reference)} className="pointer-events-none" />
                <span className="font-mono text-xs text-slate-500 w-20 truncate">{p.reference}</span>
                <span className="text-slate-700 truncate flex-1">{p.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending || !total}
        className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
      >
        {mut.isPending && <Loader2 className="animate-spin" size={16} />}
        Asignar{total ? ` (${selectedFamilies.length} fam. + ${selectedRefs.length} ref.)` : ''}
      </button>
    </div>
  )
}

function NewSupplierModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const toast = useToast()
  const [form, setForm] = useState<any>({ quote_currency: 'USD' })
  const [generated, setGenerated] = useState<string | null>(null)
  const [createdId, setCreatedId] = useState<number | null>(null)

  const mut = useMutation({
    mutationFn: () =>
      supplierPortalAdminApi.createPortalSupplier({
        code: form.code,
        name: form.name,
        username: form.username,
        password: form.password || undefined,
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_whatsapp: form.contact_whatsapp,
        quote_currency: form.quote_currency,
      }),
    onSuccess: (res: any) => {
      toast.success('Proveedor creado')
      const pw = res.account?.generated_password
      if (pw) {
        setCreatedId(res.supplier.id)
        setGenerated(pw)
      } else {
        onCreated(res.supplier.id)
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al crear'),
  })

  const valid = form.code && form.name && form.username && form.contact_email

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="font-semibold text-slate-800 mb-4">Nuevo proveedor asiático</h3>
        {generated ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
              <p className="text-green-800 font-medium mb-1">Proveedor creado. Contraseña generada (cópiala y envíasela):</p>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-1 rounded border text-slate-800">{generated}</code>
                <button onClick={() => navigator.clipboard.writeText(generated)} className="text-slate-500 hover:text-slate-700">
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <button
              onClick={() => createdId && onCreated(createdId)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-sm font-medium"
            >
              Hecho
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Código *" value={form.code || ''} onChange={(v: string) => setForm({ ...form, code: v })} placeholder="ej: CN-SHENZHEN" />
              <Field label="Nombre *" value={form.name || ''} onChange={(v: string) => setForm({ ...form, name: v })} />
              <Field label="Empresa" value={form.company_name || ''} onChange={(v: string) => setForm({ ...form, company_name: v })} />
              <Field label="Contacto" value={form.contact_name || ''} onChange={(v: string) => setForm({ ...form, contact_name: v })} />
              <Field label="Email * (para recuperar contraseña)" value={form.contact_email || ''} onChange={(v: string) => setForm({ ...form, contact_email: v })} />
              <Field label="WhatsApp" value={form.contact_whatsapp || ''} onChange={(v: string) => setForm({ ...form, contact_whatsapp: v })} />
              <Field label="Usuario *" value={form.username || ''} onChange={(v: string) => setForm({ ...form, username: v })} />
              <Field label="Contraseña (vacío = autogenerar)" value={form.password || ''} onChange={(v: string) => setForm({ ...form, password: v })} />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button
                onClick={() => mut.mutate()}
                disabled={!valid || mut.isPending}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {mut.isPending && <Loader2 className="animate-spin" size={16} />} Crear proveedor
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ======================================================================
// TAB: Pedidos de compra
// ======================================================================
const PO_STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador', sent: 'Enviado', supplier_review: 'En revisión proveedor',
  confirmed: 'Confirmado', ordered: 'Pedido en Factusol', cancelled: 'Cancelado',
}
const PO_STATUS_COLOR: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600', sent: 'bg-blue-100 text-blue-700',
  supplier_review: 'bg-amber-100 text-amber-700', confirmed: 'bg-green-100 text-green-700',
  ordered: 'bg-purple-100 text-purple-700', cancelled: 'bg-red-100 text-red-600',
}

function OrdersTab() {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showNew, setShowNew] = useState(false)
  const queryClient = useQueryClient()

  const ordersQuery = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: () => supplierPurchaseOrdersApi.list({ page: 1 }),
  })
  const orders = ordersQuery.data?.orders || []

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-xl shadow-sm p-4 lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-700">Pedidos</h2>
          <button onClick={() => setShowNew(true)} className="text-xs px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Nuevo</button>
        </div>
        <div className="space-y-1 max-h-[65vh] overflow-y-auto">
          {orders.map((o: any) => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${selectedId === o.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-slate-700">#{o.id} · {o.supplier_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${PO_STATUS_COLOR[o.status] || ''}`}>{PO_STATUS_LABEL[o.status] || o.status}</span>
              </div>
              <span className="text-xs text-slate-400">{o.line_count} líneas · {String(o.created_at).slice(0, 10)}</span>
            </button>
          ))}
          {!orders.length && !ordersQuery.isLoading && <p className="text-sm text-slate-400 px-3 py-4">No hay pedidos.</p>}
        </div>
      </div>

      <div className="lg:col-span-2">
        {selectedId ? (
          <OrderDetail orderId={selectedId} onChanged={() => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })} />
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-10 text-center text-slate-400">Selecciona un pedido.</div>
        )}
      </div>

      {showNew && (
        <NewOrderModal
          onClose={() => setShowNew(false)}
          onDone={() => {
            setShowNew(false)
            queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
          }}
        />
      )}
    </div>
  )
}

function OrderDetail({ orderId, onChanged }: { orderId: number; onChanged: () => void }) {
  const toast = useToast()
  const [factusolNum, setFactusolNum] = useState('')
  const query = useQuery({ queryKey: ['purchase-order', orderId], queryFn: () => supplierPurchaseOrdersApi.get(orderId) })
  const order = query.data

  const refetch = () => { query.refetch(); onChanged() }
  const sendMut = useMutation({ mutationFn: () => supplierPurchaseOrdersApi.send(orderId), onSuccess: () => { toast.success('Pedido enviado'); refetch() }, onError: () => toast.error('Solo se envían borradores') })
  const markMut = useMutation({ mutationFn: () => supplierPurchaseOrdersApi.markOrdered(orderId, factusolNum || undefined), onSuccess: () => { toast.success('Marcado como pedido'); refetch() } })
  const cancelMut = useMutation({ mutationFn: () => supplierPurchaseOrdersApi.cancel(orderId), onSuccess: () => { toast.success('Cancelado'); refetch() } })

  if (query.isLoading || !order) return <div className="bg-white rounded-xl shadow-sm p-10 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Pedido #{order.id} · {order.supplier_name}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${PO_STATUS_COLOR[order.status] || ''}`}>{PO_STATUS_LABEL[order.status] || order.status}</span>
        </div>
        <div className="flex items-center gap-2">
          {order.status === 'draft' && (
            <button onClick={() => sendMut.mutate()} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center gap-1">
              <Send size={14} /> Enviar al proveedor
            </button>
          )}
          <a href={supplierPurchaseOrdersApi.exportUrl(order.id)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm flex items-center gap-1 hover:bg-slate-50">
            <Download size={14} /> Exportar CSV
          </a>
        </div>
      </div>

      {order.supplier_note && <div className="text-sm bg-slate-50 rounded-lg p-3"><span className="text-slate-500">Nota proveedor:</span> {order.supplier_note}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-2 py-2">Referencia</th><th className="px-2 py-2">Producto</th>
              <th className="px-2 py-2">Cant. ped.</th><th className="px-2 py-2">USD ped.</th>
              <th className="px-2 py-2">Cant. conf.</th><th className="px-2 py-2">USD conf.</th><th className="px-2 py-2">Plazo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {order.lines.map((l: any) => (
              <tr key={l.id}>
                <td className="px-2 py-2 font-mono text-xs">{l.our_reference}</td>
                <td className="px-2 py-2">{l.name}</td>
                <td className="px-2 py-2">{l.qty_requested}</td>
                <td className="px-2 py-2">{l.unit_price_usd ?? '—'}</td>
                <td className="px-2 py-2 font-medium">{l.qty_confirmed ?? '—'}</td>
                <td className="px-2 py-2 font-medium">{l.unit_price_confirmed_usd ?? '—'}</td>
                <td className="px-2 py-2">{l.lead_time_days ? `${l.lead_time_days}d` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {order.status === 'confirmed' && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <input className="border border-slate-300 rounded px-2 py-1 text-sm w-44" placeholder="Nº pedido Factusol" value={factusolNum} onChange={(e) => setFactusolNum(e.target.value)} />
          <button onClick={() => markMut.mutate()} className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm">Marcar como pedido</button>
        </div>
      )}
      {order.status === 'ordered' && order.factusol_order_number && (
        <p className="text-sm text-purple-700">Pedido Factusol: <strong>{order.factusol_order_number}</strong></p>
      )}
      {['draft', 'sent', 'supplier_review', 'confirmed'].includes(order.status) && (
        <button onClick={() => cancelMut.mutate()} className="text-xs text-red-500 hover:underline">Cancelar pedido</button>
      )}

      <div className="pt-4 border-t border-slate-100">
        <h4 className="text-sm font-semibold text-slate-600 mb-2">Mensajes del pedido</h4>
        <AdminMessageThread supplierId={order.supplier_id} context={{ order_id: order.id }} />
      </div>
    </div>
  )
}

function AdminMessageThread({ supplierId, context }: { supplierId: number; context: { product_id?: number; order_id?: number; proposal_id?: number } }) {
  const [messages, setMessages] = useState<any[]>([])
  const [body, setBody] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [sending, setSending] = useState(false)

  const load = () => {
    supplierPortalAdminApi.listMessages({ supplier_id: supplierId, ...context }).then((d) => setMessages(d.messages || []))
  }
  useEffect(() => { load() }, [supplierId, context.order_id, context.product_id, context.proposal_id])

  const send = async () => {
    if (!body && !file) return
    setSending(true)
    try {
      const fd = new FormData()
      fd.append('supplier_id', String(supplierId))
      if (body) fd.append('body', body)
      if (context.order_id) fd.append('order_id', String(context.order_id))
      if (context.product_id) fd.append('product_id', String(context.product_id))
      if (context.proposal_id) fd.append('proposal_id', String(context.proposal_id))
      if (file) fd.append('files', file)
      await supplierPortalAdminApi.postMessage(fd)
      setBody(''); setFile(null); load()
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === 'staff' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === 'staff' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
              {m.body && <div className="whitespace-pre-wrap">{m.body}</div>}
              {m.attachments?.map((a: any) => (
                <a key={a.id} href={supplierPortalAdminApi.attachmentUrl(a.id)} target="_blank" rel="noreferrer"
                  className={`block text-xs underline mt-1 ${m.sender === 'staff' ? 'text-blue-100' : 'text-blue-600'}`}>
                  📎 {a.filename}
                </a>
              ))}
              <div className={`text-[10px] mt-1 ${m.sender === 'staff' ? 'text-blue-200' : 'text-slate-400'}`}>{String(m.created_at).slice(0, 16).replace('T', ' ')}</div>
            </div>
          </div>
        ))}
        {!messages.length && <p className="text-xs text-slate-400">Sin mensajes.</p>}
      </div>
      <div className="flex items-center gap-2">
        <input className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder="Escribe un mensaje…" value={body}
          onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} />
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs w-28" />
        <button onClick={send} disabled={sending || (!body && !file)} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
          {sending ? '…' : 'Enviar'}
        </button>
      </div>
    </div>
  )
}

function NewOrderModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const toast = useToast()
  const { user } = useAuth()
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([])
  const [refQuery, setRefQuery] = useState('')
  const [debouncedRef, setDebouncedRef] = useState('')
  // líneas por referencia: { ref: {name, qty, price} }
  const [lines, setLines] = useState<Record<string, { name: string; qty: string; price: string }>>({})
  const [sendNow, setSendNow] = useState(true)

  const suppliersQuery = useQuery({ queryKey: ['suppliers', 'portal'], queryFn: () => supplierCatalogsApi.list({ portal_only: true }) })
  const suppliers = suppliersQuery.data || []

  useEffect(() => {
    const t = setTimeout(() => setDebouncedRef(refQuery.trim()), 350)
    return () => clearTimeout(t)
  }, [refQuery])
  const searchQuery = useQuery({
    queryKey: ['po-product-search', debouncedRef],
    queryFn: () => supplierPortalAdminApi.searchFactusolProducts(debouncedRef),
    enabled: debouncedRef.length >= 2,
  })

  const toggleSupplier = (id: number) => setSelectedSuppliers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  const addRef = (p: any) => setLines((prev) => prev[p.reference] ? prev : { ...prev, [p.reference]: { name: p.name, qty: '', price: '' } })
  const removeRef = (ref: string) => setLines((prev) => { const n = { ...prev }; delete n[ref]; return n })
  const setLine = (ref: string, k: 'qty' | 'price', v: string) => setLines((prev) => ({ ...prev, [ref]: { ...prev[ref], [k]: v } }))

  const validLines = Object.entries(lines).filter(([, v]) => Number(v.qty) > 0)

  const createMut = useMutation({
    mutationFn: () =>
      supplierPurchaseOrdersApi.createMulti({
        supplier_ids: selectedSuppliers,
        created_by: user?.id,
        send: sendNow,
        lines: validLines.map(([ref, v]) => ({ our_reference: ref, name: v.name, qty: Number(v.qty), unit_price_usd: v.price ? Number(v.price) : null })),
      }),
    onSuccess: (res: any) => { toast.success(`${res.created} pedido(s) creado(s)${sendNow ? ' y enviado(s)' : ''}`); onDone() },
    onError: () => toast.error('Error al crear pedidos'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="font-semibold text-slate-800 mb-4">Nueva consulta de pedido</h3>

        {/* Proveedores (multiselección) */}
        <label className="text-sm text-slate-600 block mb-1">Proveedores (uno o varios)</label>
        <div className="border border-slate-200 rounded-lg p-2 mb-4 max-h-32 overflow-y-auto flex flex-wrap gap-2">
          {suppliers.map((s: any) => (
            <button key={s.id} onClick={() => toggleSupplier(s.id)}
              className={`text-sm px-3 py-1 rounded-full border ${selectedSuppliers.includes(s.id) ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              {s.name}
            </button>
          ))}
          {!suppliers.length && <span className="text-sm text-slate-400 px-1">No hay proveedores del portal.</span>}
        </div>

        {/* Búsqueda de referencias */}
        <label className="text-sm text-slate-600 block mb-1">Productos a consultar (busca por CODART/REFART/nombre)</label>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg w-full text-sm" placeholder="Buscar artículo…" value={refQuery} onChange={(e) => setRefQuery(e.target.value)} />
        </div>
        {debouncedRef.length >= 2 && (
          <div className="border border-slate-100 rounded-lg max-h-40 overflow-y-auto mb-3">
            {searchQuery.isLoading ? (
              <div className="p-3 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" size={16} /></div>
            ) : !(searchQuery.data || []).length ? (
              <p className="p-3 text-sm text-slate-400">Sin resultados.</p>
            ) : (searchQuery.data || []).map((p: any) => (
              <button key={p.reference} onClick={() => addRef(p)} className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-slate-50 ${lines[p.reference] ? 'bg-green-50' : ''}`}>
                <span className="font-mono text-xs text-slate-500 w-20 truncate">{p.reference}</span>
                <span className="text-slate-700 truncate flex-1">{p.name}</span>
                {lines[p.reference] && <Check size={14} className="text-green-600" />}
              </button>
            ))}
          </div>
        )}

        {/* Líneas seleccionadas */}
        {validLines.length === 0 && Object.keys(lines).length === 0 ? null : (
          <div className="border border-slate-100 rounded-lg overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr><th className="px-2 py-2">Referencia</th><th className="px-2 py-2">Producto</th><th className="px-2 py-2">Cantidad</th><th className="px-2 py-2">Precio USD</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(lines).map(([ref, v]) => (
                  <tr key={ref}>
                    <td className="px-2 py-1 font-mono text-xs">{ref}</td>
                    <td className="px-2 py-1 truncate max-w-[180px]">{v.name}</td>
                    <td className="px-2 py-1"><input type="number" min="0" className="w-20 border border-slate-300 rounded px-2 py-1" value={v.qty} onChange={(e) => setLine(ref, 'qty', e.target.value)} /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" min="0" className="w-24 border border-slate-300 rounded px-2 py-1" value={v.price} onChange={(e) => setLine(ref, 'price', e.target.value)} /></td>
                    <td className="px-2 py-1"><button onClick={() => removeRef(ref)} className="text-red-400 hover:text-red-600"><X size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={sendNow} onChange={(e) => setSendNow(e.target.checked)} />
            Enviar a los proveedores ahora
          </label>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={() => createMut.mutate()} disabled={!selectedSuppliers.length || !validLines.length || createMut.isPending}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2">
              {createMut.isPending && <Loader2 className="animate-spin" size={16} />}
              Crear ({selectedSuppliers.length} prov. × {validLines.length} líneas)
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ======================================================================
// TAB: Propuestas de productos
// ======================================================================
function ProposalsTab() {
  const toast = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: ['product-proposals'], queryFn: () => supplierPortalAdminApi.listProposals({ status: 'pending' }) })
  const proposals = query.data?.proposals || []
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['product-proposals'] })

  const acceptMut = useMutation({
    mutationFn: (id: number) => supplierPortalAdminApi.acceptProposal(id, { reviewed_by: user?.id }),
    onSuccess: () => { toast.success('Propuesta aceptada (producto creado)'); refresh() },
    onError: () => toast.error('Error al aceptar'),
  })
  const rejectMut = useMutation({
    mutationFn: (id: number) => supplierPortalAdminApi.rejectProposal(id, { reviewed_by: user?.id }),
    onSuccess: () => { toast.success('Propuesta rechazada'); refresh() },
  })

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="font-semibold text-slate-700 mb-4">Propuestas pendientes</h2>
      {query.isLoading ? (
        <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>
      ) : !proposals.length ? (
        <div className="p-10 text-center text-slate-400">No hay propuestas pendientes.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {proposals.map((p: any) => (
            <div key={p.id} className="border border-slate-200 rounded-xl p-4">
              <div className="flex gap-3">
                {p.has_image ? (
                  <img src={supplierPortalAdminApi.proposalImageUrl(p.id)} alt="" className="w-20 h-20 object-contain rounded border border-slate-100" />
                ) : (
                  <div className="w-20 h-20 rounded bg-slate-100 flex items-center justify-center text-slate-300"><Package size={22} /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.supplier_name}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {p.cost_price_usd ? `$${p.cost_price_usd} USD` : ''} {p.ean ? `· EAN ${p.ean}` : ''}
                  </div>
                  {p.brand && <div className="text-xs text-slate-400">{p.brand} · {p.category}</div>}
                </div>
              </div>
              {p.description && <p className="text-xs text-slate-500 mt-2 line-clamp-3">{p.description}</p>}
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => acceptMut.mutate(p.id)} className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center justify-center gap-1">
                  <Check size={14} /> Aceptar
                </button>
                <button onClick={() => rejectMut.mutate(p.id)} className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200">
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ======================================================================
// TAB: Histórico / evolución de precio por producto
// ======================================================================
function PriceHistoryTab() {
  const [q, setQ] = useState('')
  const [reference, setReference] = useState<string | null>(null)

  const searchQuery = useQuery({
    queryKey: ['price-evo-search', q],
    queryFn: () => supplierPortalAdminApi.searchPriceEvolution(q),
    enabled: q.trim().length >= 1 && !reference,
  })
  const evoQuery = useQuery({
    queryKey: ['price-evo', reference],
    queryFn: () => supplierPortalAdminApi.getPriceEvolution(reference!),
    enabled: !!reference,
  })

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg w-full"
            placeholder="Buscar referencia (CODART), nombre…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setReference(null) }}
          />
        </div>

        {!reference && q.trim() && (
          <div className="mt-3 border border-slate-100 rounded-lg max-h-72 overflow-y-auto">
            {searchQuery.isLoading ? (
              <div className="p-4 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" size={18} /></div>
            ) : !(searchQuery.data?.results || []).length ? (
              <p className="p-4 text-sm text-slate-400">Sin resultados.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {searchQuery.data.results.map((r: any) => (
                    <tr key={r.reference} className="hover:bg-slate-50 cursor-pointer" onClick={() => setReference(r.reference)}>
                      <td className="px-3 py-2 font-mono text-xs">{r.reference}</td>
                      <td className="px-3 py-2 text-slate-700 truncate">{r.name}</td>
                      <td className="px-3 py-2 text-right text-xs text-slate-500">{r.supplier_count} prov.</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {reference && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-end mb-2">
            <button onClick={() => setReference(null)} className="text-sm text-blue-600">&larr; Volver a la búsqueda</button>
          </div>
          <PriceEvolution reference={reference} />
        </div>
      )}
    </div>
  )
}

// Gráfica + tabla de evolución de precio de una referencia (reutilizable)
function PriceEvolution({ reference }: { reference: string }) {
  const evoQuery = useQuery({
    queryKey: ['price-evo', reference],
    queryFn: () => supplierPortalAdminApi.getPriceEvolution(reference),
  })
  const evo = evoQuery.data
  const chartData = (() => {
    if (!evo) return []
    const byDate: Record<string, any> = {}
    for (const s of evo.series || []) {
      for (const p of s.points || []) {
        byDate[p.date] = byDate[p.date] || { date: p.date }
        byDate[p.date][s.supplier_name] = p.cost_price
      }
    }
    return Object.values(byDate).sort((a: any, b: any) => a.date.localeCompare(b.date))
  })()
  const supplierNames: string[] = evo ? (evo.series || []).map((s: any) => s.supplier_name) : []

  if (evoQuery.isLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>

  return (
    <>
      <div className="mb-4">
        <h3 className="font-semibold text-slate-800">{evo?.name}</h3>
        <span className="font-mono text-xs text-slate-500">{evo?.reference}</span>
      </div>
      {!chartData.length ? (
        <p className="text-sm text-slate-400 py-8 text-center">Aún no hay histórico de precios aprobados para esta referencia.</p>
      ) : (
        <div className="h-72 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <Tooltip formatter={(v: any) => `${v} €`} />
              <Legend />
              {supplierNames.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <h4 className="text-sm font-semibold text-slate-600 mb-2">Todas las cotizaciones</h4>
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left sticky top-0">
            <tr>
              <th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Proveedor</th>
              <th className="px-3 py-2">USD</th><th className="px-3 py-2">EUR</th>
              <th className="px-3 py-2">Estado</th><th className="px-3 py-2">Origen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(evo?.submissions || []).map((s: any, i: number) => (
              <tr key={i}>
                <td className="px-3 py-2 text-slate-500">{String(s.created_at).slice(0, 16).replace('T', ' ')}</td>
                <td className="px-3 py-2">{s.supplier_name}</td>
                <td className="px-3 py-2">{s.submitted_cost_price ?? '—'}</td>
                <td className="px-3 py-2 font-medium">{s.converted_cost_price_eur ?? '—'}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'approved' ? 'bg-green-100 text-green-700' : s.status === 'rejected' ? 'bg-red-100 text-red-600' : s.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{s.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-400">{s.source}</td>
              </tr>
            ))}
            {!(evo?.submissions || []).length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Sin cotizaciones.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  )
}

// Modal con la evolución de precio (acceso directo desde fichas/filas)
function PriceEvolutionModal({ reference, onClose }: { reference: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end -mt-2 -mr-2 mb-1">
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <PriceEvolution reference={reference} />
      </div>
    </div>
  )
}

// ======================================================================
// TAB: Ranking de rendimiento
// ======================================================================
function RankingTab() {
  const query = useQuery({ queryKey: ['supplier-ranking'], queryFn: () => supplierPortalAdminApi.getRanking() })
  const ranking = query.data?.ranking || []

  const scoreColor = (s: number | null) =>
    s == null ? 'text-slate-400' : s >= 70 ? 'text-green-600' : s >= 40 ? 'text-amber-600' : 'text-red-600'

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="font-semibold text-slate-700 mb-1">Ranking de proveedores</h2>
      <p className="text-sm text-slate-500 mb-4">Score compuesto: frescura de precios, competitividad, cumplimiento de pedidos y tiempo de respuesta.</p>
      {query.isLoading ? (
        <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>
      ) : !ranking.length ? (
        <div className="p-10 text-center text-slate-400">Aún no hay proveedores del portal con datos.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2">#</th><th className="px-3 py-2">Proveedor</th>
                <th className="px-3 py-2">Score</th><th className="px-3 py-2">Competitividad</th>
                <th className="px-3 py-2">Frescura</th><th className="px-3 py-2">Respuesta</th>
                <th className="px-3 py-2">Cotiz. (A/R/P)</th><th className="px-3 py-2">Pedidos</th>
                <th className="px-3 py-2">Plazo medio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ranking.map((r: any, i: number) => (
                <tr key={r.supplier_id}>
                  <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                  <td className={`px-3 py-2 font-bold ${scoreColor(r.score)}`}>{r.score ?? '—'}</td>
                  <td className="px-3 py-2">{r.competitiveness_pct != null ? `${r.competitiveness_pct}%` : '—'}</td>
                  <td className="px-3 py-2">{r.price_freshness_days != null ? `${r.price_freshness_days}d` : '—'}</td>
                  <td className="px-3 py-2">{r.avg_response_hours != null ? `${r.avg_response_hours}h` : '—'}</td>
                  <td className="px-3 py-2 text-xs">{r.quotes_approved}/{r.quotes_rejected}/{r.quotes_pending}</td>
                  <td className="px-3 py-2 text-xs">{r.orders_confirmed}/{r.orders_total}</td>
                  <td className="px-3 py-2">{r.avg_lead_time_days != null ? `${r.avg_lead_time_days}d` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AssignedProductsCard({ supplierId }: { supplierId: number }) {
  const [search, setSearch] = useState('')
  const [evoRef, setEvoRef] = useState<string | null>(null)
  const query = useQuery({
    queryKey: ['assigned-products', supplierId, search],
    queryFn: () => supplierCatalogsApi.listProducts(supplierId, { page: 1, page_size: 50, search: search || undefined }),
  })
  const products = query.data?.products || []

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
        <Package size={18} /> Productos asignados
      </h3>
      <p className="text-sm text-slate-500 mb-3">Cada producto enlaza a su evolución de precio (histórico por proveedor).</p>
      <div className="relative mb-3 max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
        <input className="pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg w-full text-sm" placeholder="Buscar…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {query.isLoading ? (
          <div className="p-4 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" size={16} /></div>
        ) : products.map((p: any) => (
          <div key={p.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2 text-sm">
            <div className="flex-1 min-w-0">
              <div className="text-slate-800 truncate">{p.name}</div>
              <div className="font-mono text-xs text-slate-400">{p.our_reference || p.supplier_reference}</div>
            </div>
            <span className="text-xs text-slate-500">{p.cost_price ? `${p.cost_price} €` : '—'}</span>
            {p.our_reference && (
              <button onClick={() => setEvoRef(p.our_reference)} title="Ver evolución de precio"
                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                <LineChartIcon size={15} />
              </button>
            )}
          </div>
        ))}
        {!query.isLoading && !products.length && <p className="text-sm text-slate-400">Sin productos asignados.</p>}
      </div>
      {evoRef && <PriceEvolutionModal reference={evoRef} onClose={() => setEvoRef(null)} />}
    </div>
  )
}

function DocumentsCard({ supplierId }: { supplierId: number }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const query = useQuery({ queryKey: ['supplier-documents', supplierId], queryFn: () => supplierPortalAdminApi.listDocuments(supplierId) })
  const docs = query.data?.documents || []
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['supplier-documents', supplierId] })

  const uploadMut = useMutation({
    mutationFn: () => {
      const fd = new FormData()
      fd.append('supplier_id', String(supplierId))
      fd.append('file', file as File)
      if (note) fd.append('note', note)
      return supplierPortalAdminApi.uploadDocument(fd)
    },
    onSuccess: () => { toast.success('Documento subido'); setFile(null); setNote(''); refresh() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error al subir'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => supplierPortalAdminApi.deleteDocument(id),
    onSuccess: () => { toast.success('Documento eliminado'); refresh() },
  })

  const fmtSize = (b: number) => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
        <Download size={18} /> Documentos de tarifa
      </h3>
      <p className="text-sm text-slate-500 mb-4">Ficheros que el proveedor (o tú) sube en su propio formato (Excel/PDF/Word/imagen…), archivados con fecha.</p>

      <div className="flex items-center gap-2 mb-4">
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm flex-1" />
        <input className="border border-slate-300 rounded px-2 py-1 text-sm w-32" placeholder="Nota (opc.)" value={note} onChange={(e) => setNote(e.target.value)} />
        <button onClick={() => uploadMut.mutate()} disabled={!file || uploadMut.isPending} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50">
          {uploadMut.isPending ? '…' : 'Subir'}
        </button>
      </div>

      <div className="space-y-1 max-h-72 overflow-y-auto">
        {docs.map((d: any) => (
          <div key={d.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2 text-sm">
            <a href={supplierPortalAdminApi.documentDownloadUrl(d.id)} className="flex-1 text-blue-600 hover:underline truncate">📄 {d.filename}</a>
            <span className="text-xs text-slate-400">{fmtSize(d.size || 0)}</span>
            <span className="text-xs text-slate-400">{String(d.created_at).slice(0, 10)}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.uploaded_by === 'staff' ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-600'}`}>{d.uploaded_by === 'staff' ? 'JVS' : 'Prov.'}</span>
            <button onClick={() => deleteMut.mutate(d.id)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
          </div>
        ))}
        {!docs.length && <p className="text-sm text-slate-400">Sin documentos.</p>}
      </div>
    </div>
  )
}

function Field({ label, value, onChange, placeholder }: any) {
  return (
    <label className="text-sm block">
      <span className="block text-slate-600 mb-1">{label}</span>
      <input
        className="w-full border border-slate-300 rounded-lg px-3 py-2"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

// ======================================================================
// TAB 2: Revisión de cotizaciones
// ======================================================================
function ReviewTab() {
  const toast = useToast()
  const [showFxHistory, setShowFxHistory] = useState(false)
  const [evoRef, setEvoRef] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['quote-submissions'],
    queryFn: () => supplierPortalAdminApi.listSubmissions({ status: 'applied', page_size: 200 }),
  })
  const data = query.data
  const submissions = data?.submissions || []

  const fxQuery = useQuery({ queryKey: ['fx-info'], queryFn: () => supplierPortalAdminApi.getFx() })
  const fxInfo = fxQuery.data
  const fxHistoryQuery = useQuery({
    queryKey: ['fx-history'],
    queryFn: () => supplierPortalAdminApi.getFxHistory(60),
    enabled: showFxHistory,
  })
  const refreshFxMut = useMutation({
    mutationFn: () => supplierPortalAdminApi.refreshFx(),
    onSuccess: (res: any) => {
      if (res.updated) toast.success(`Tasa BCE actualizada: ${res.USD_EUR}`)
      else toast.info?.(`Sin cambios (${res.reason || 'ok'})`)
      fxQuery.refetch(); fxHistoryQuery.refetch()
    },
  })

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-sm text-slate-500 mb-3">
        Cambios de precio/disponibilidad de los proveedores (se aplican automáticamente; informativos para compra). Las filas en rojo son cambios anómalos a revisar.
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm mb-4">
        <span className="text-slate-600">Tasa USD→EUR:</span>
        <span className="font-medium">{fxInfo?.USD_EUR ?? data?.fx_rate}</span>
        {fxInfo?.source === 'ecb' && fxInfo?.fetched_at && <span className="text-xs text-slate-400">BCE {String(fxInfo.fetched_at).slice(0, 10)}</span>}
        <button onClick={() => refreshFxMut.mutate()} disabled={refreshFxMut.isPending} className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
          {refreshFxMut.isPending ? '…' : 'Actualizar BCE'}
        </button>
        <button onClick={() => setShowFxHistory((v) => !v)} className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50">
          {showFxHistory ? 'Ocultar histórico' : 'Histórico tasa'}
        </button>
      </div>

      {showFxHistory && (
        <div className="mb-4 border border-slate-200 rounded-lg p-3">
          <h4 className="text-sm font-medium text-slate-600 mb-2">Histórico de tipo de cambio (BCE)</h4>
          {fxHistoryQuery.isLoading ? (
            <Loader2 className="animate-spin text-slate-400" size={16} />
          ) : !(fxHistoryQuery.data?.history || []).length ? (
            <p className="text-xs text-slate-400">Aún no hay histórico. Se irá llenando con cada actualización diaria del BCE.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 text-left">
                  <tr><th className="py-1">Fecha</th><th className="py-1">USD→EUR</th><th className="py-1">1 EUR = USD</th><th className="py-1">Fuente</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {fxHistoryQuery.data.history.map((h: any) => (
                    <tr key={h.date}>
                      <td className="py-1">{h.date}</td>
                      <td className="py-1 font-medium">{h.usd_eur}</td>
                      <td className="py-1 text-slate-500">{h.usd_per_eur ?? '—'}</td>
                      <td className="py-1 text-slate-400">{h.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {query.isLoading ? (
        <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>
      ) : !submissions.length ? (
        <div className="p-10 text-center text-slate-400">Aún no hay cambios de precio.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Fecha</th>
                <th className="px-3 py-2 font-medium">Proveedor</th>
                <th className="px-3 py-2 font-medium">Referencia</th>
                <th className="px-3 py-2 font-medium">Producto</th>
                <th className="px-3 py-2 font-medium">Precio USD</th>
                <th className="px-3 py-2 font-medium">EUR</th>
                <th className="px-3 py-2 font-medium">Coste anterior €</th>
                <th className="px-3 py-2 font-medium">Stock</th>
                <th className="px-3 py-2 font-medium">Origen</th>
                <th className="px-3 py-2 font-medium text-right">Gráfica</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {submissions.map((s: any) => (
                <tr key={s.id} className={s.flagged ? 'bg-red-50' : ''}>
                  <td className="px-3 py-2 text-xs text-slate-500">{String(s.created_at).slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-2 text-slate-700">{s.supplier_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{s.our_reference || s.supplier_reference}</td>
                  <td className="px-3 py-2 max-w-xs truncate">{s.product_name}</td>
                  <td className="px-3 py-2">
                    {s.flagged && <AlertTriangle size={14} className="inline text-red-500 mr-1" />}
                    {s.submitted_cost_price?.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">{s.converted_cost_price_eur?.toFixed?.(2) ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-400">{s.previous_cost_price?.toFixed?.(2) ?? '—'}</td>
                  <td className="px-3 py-2">
                    {s.submitted_stock ?? '—'}
                    {s.submitted_stock_status ? <span className="text-xs text-slate-400"> ({s.submitted_stock_status})</span> : null}
                    {s.submitted_lead_time_days != null ? <span className="text-xs text-amber-600"> · {s.submitted_lead_time_days}d</span> : null}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-400">{s.source}</td>
                  <td className="px-3 py-2 text-right">
                    {s.our_reference && (
                      <button onClick={() => setEvoRef(s.our_reference)} className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100" title="Ver evolución de precio">
                        <LineChartIcon size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {evoRef && <PriceEvolutionModal reference={evoRef} onClose={() => setEvoRef(null)} />}
    </div>
  )
}
